import { Watch } from './watchesApi';

/**
 * Matching a news headline against a user's watch list.
 *
 * This runs entirely in the browser. The news JSON is already downloaded in
 * full, so every title is in memory -- there is nothing to precompute and no
 * pipeline involved. It also means brands picked from the canonical list,
 * brands typed by hand, and models are all matched the same way, since the
 * client knows the user's whole list.
 */

export interface MatchTerm {
  /** What to show the user when this term hits, e.g. "Tudor Ranger". */
  label: string;
  /** Folded needle, padded with spaces for whole-word comparison. */
  needle: string;
}

/**
 * Lowercase, strip accents, and reduce every run of punctuation to a single
 * space -- so "A. Lange & Söhne" and "a lange sohne" converge, and
 * "TAG-Heuer" matches "TAG Heuer".
 */
export const fold = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Padded so indexOf() only hits on whole words: " ball " never matches "football". */
const padded = (s: string) => ` ${s} `;

/**
 * Extra spellings for brands whose canonical form rarely appears verbatim in
 * headlines. Keyed by folded canonical name; values are additional folded
 * needles. Deliberately short -- every alias is a chance for a false positive.
 */
const BRAND_ALIASES: Record<string, string[]> = {
  'a lange sohne': ['lange'],
  bulgari: ['bvlgari'],
  'glashutte original': ['glashutte'],
  'tag heuer': ['tag'],
};

/**
 * Build the match terms for a watch list.
 *
 * A watch hits on its brand OR its model. Brand-level is the useful
 * granularity -- if you track a Tudor Ranger you almost certainly want other
 * Tudor news -- while the model catches headlines that name the watch without
 * the brand.
 *
 * Model needles are matched as a whole phrase, so "Black Bay 58" will not fire
 * on an unrelated headline containing only "Bay".
 */
export function buildTerms(watches: Watch[]): MatchTerm[] {
  const byNeedle = new Map<string, MatchTerm>();

  for (const w of watches) {
    const label = `${w.brand} ${w.model}`.trim();
    const brand = fold(w.brand);
    const model = fold(w.model);

    const needles = [brand, model, ...(BRAND_ALIASES[brand] ?? [])];

    for (const n of needles) {
      // One-and-two-character needles match far too much to be useful.
      if (n.length < 3) continue;
      const key = padded(n);
      // First watch to claim a needle owns the label; two Tudors should not
      // produce two identical "Tudor" badges on the same article.
      if (!byNeedle.has(key)) byNeedle.set(key, { label, needle: key });
    }
  }

  // forEach rather than spreading .values(): the build targets ES5, where
  // iterating a Map directly is not available.
  const out: MatchTerm[] = [];
  byNeedle.forEach(term => out.push(term));
  return out;
}

/** Which terms appear in this title. Empty array means no match. */
export function matchTitle(title: string, terms: MatchTerm[]): MatchTerm[] {
  if (terms.length === 0) return [];
  const hay = padded(fold(title));
  return terms.filter(t => hay.includes(t.needle));
}

/** Convenience for filtering: does this title hit any term at all? */
export const titleHits = (title: string, terms: MatchTerm[]): boolean =>
  matchTitle(title, terms).length > 0;

/** Distinct watch labels that matched, for display. */
export const matchedLabels = (title: string, terms: MatchTerm[]): string[] => {
  const labels = matchTitle(title, terms).map(t => t.label);
  return labels.filter((label, i) => labels.indexOf(label) === i);
};
